import {
  tryLoadDependencies,
  tryLoadLightDOMStyles,
  WebAwesomeComponent,
} from '@/components/webcomponents/Global/loadDependency'
import { getNavEnterpriseMenuStyles } from './NavEnterpriseMenu.styles'
import '@/components/webcomponents/NavEnterprise/NavEnterpriseMenuItem/NavEnterpriseMenuItem'
import type { NavEnterpriseMenuItem } from '@/components/webcomponents/NavEnterprise/NavEnterpriseMenuItem/NavEnterpriseMenuItem'
import type { NavEnterpriseMenuItemData } from '@/components/webcomponents/NavEnterprise/NavEnterpriseMenu/types/NavEnterpriseMenu.type'

const DefaultNavLabel = 'Enterprise Navigation'
const LoadingMessage = 'Loading navigation'
const DefaultSkeletonCount = 4
const MaxSkeletonCount = 12

const t = document.createElement('template')
t.innerHTML = `
  <nav class="nav-enterprise-menu app-link-group" role="navigation" aria-label="${DefaultNavLabel}">
    <span class="loading-status" role="status"></span>
    <div class="skeleton-rows" aria-hidden="true"></div>
    <slot></slot>
  </nav>
`
const TemplateNavEnterpriseMenu = t

class NavEnterpriseMenu extends HTMLElement {
  private collapseHandler: ((e: Event) => void) | null = null
  private parentNavEnterprise: HTMLElement | null = null
  private itemData: NavEnterpriseMenuItemData[] | null = null

  static get observedAttributes() {
    return ['label', 'loading', 'skeleton-count']
  }

  constructor() {
    super()

    tryLoadDependencies(document, [WebAwesomeComponent.Skeleton])
    tryLoadLightDOMStyles('NavEnterpriseMenu.css')

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.appendChild(getNavEnterpriseMenuStyles().content.cloneNode(true))
    shadow.appendChild(TemplateNavEnterpriseMenu.content.cloneNode(true))
  }

  connectedCallback() {
    document.AscendAwesome?.LogManager?.logDebug('NavEnterpriseMenu connectedCallback')

    // Frameworks may set `items` before this element is upgraded.
    if (Object.prototype.hasOwnProperty.call(this, 'items')) {
      const pending = (this as unknown as { items: NavEnterpriseMenuItemData[] }).items
      delete (this as unknown as { items?: unknown }).items
      this.items = pending
    }

    // Listen for collapse events from parent NavEnterprise
    this.collapseHandler = (e: Event) => this.onParentCollapse(e)
    this.parentNavEnterprise = this.closest('ts-nav-enterprise') as HTMLElement | null
    this.parentNavEnterprise?.addEventListener('nav-collapse-change', this.collapseHandler)

    // Check if parent is already collapsed
    if (this.parentNavEnterprise?.classList.contains('collapsed')) {
      this.setMenuCollapsed(true)
    } else {
      this.setMenuCollapsed(false)
    }
  }

  disconnectedCallback() {
    if (this.collapseHandler) {
      this.parentNavEnterprise?.removeEventListener('nav-collapse-change', this.collapseHandler)
      this.collapseHandler = null
    }
    this.parentNavEnterprise = null
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (oldValue === newValue) return
    if (name === 'label') {
      this.shadowRoot?.querySelector('nav')?.setAttribute('aria-label', newValue || DefaultNavLabel)
    } else {
      this.renderLoading()
    }
  }

  get loading(): boolean {
    return this.hasAttribute('loading')
  }

  set loading(value: boolean) {
    this.toggleAttribute('loading', Boolean(value))
  }

  get skeletonCount(): number {
    const count = Number.parseInt(this.getAttribute('skeleton-count') ?? '', 10)
    return Number.isNaN(count)
      ? DefaultSkeletonCount
      : Math.min(Math.max(count, 1), MaxSkeletonCount)
  }

  set skeletonCount(value: number) {
    this.setAttribute('skeleton-count', String(value))
  }

  get items(): NavEnterpriseMenuItemData[] {
    return this.itemData ?? []
  }

  set items(value: NavEnterpriseMenuItemData[]) {
    this.itemData = Array.isArray(value) ? value : []
    this.renderItems()
  }

  private renderItems() {
    if (this.itemData === null) return

    const items = this.itemData.map((data) => {
      // defaults are now set in renderIcon() of the menu item component
      // single code path
      const item = document.createElement('ts-nav-enterprise-menu-item') as NavEnterpriseMenuItem
      item.label = data.label
      item.href = data.href
      item.icon = data.iconName ?? ''
      item.iconLibrary = data.iconLibrary ?? ''
      item.iconFamily = data.iconFamily ?? ''
      item.iconVariant = data.iconVariant ?? ''
      item.iconSrc = data.iconSrc ?? ''
      item.active = Boolean(data.active)
      item.target = data.target ?? ''
      item.ariaLabel = data.ariaLabel ?? ''
      return item
    })

    this.replaceChildren(...items)
  }

  private renderLoading() {
    const nav = this.shadowRoot?.querySelector('nav')
    const rows = this.shadowRoot?.querySelector('.skeleton-rows')
    const status = this.shadowRoot?.querySelector('.loading-status')
    if (!nav || !rows || !status) return

    const loading = this.loading
    nav.toggleAttribute('aria-busy', loading)
    status.textContent = loading ? LoadingMessage : ''
    rows.replaceChildren(
      ...(loading
        ? Array.from({ length: this.skeletonCount }, () => {
          const row = document.createElement('div')
          row.className = 'skeleton-row'
          row.innerHTML =
            '<wa-skeleton class="skeleton-icon" effect="sheen"></wa-skeleton>' +
            '<wa-skeleton class="skeleton-label" effect="sheen"></wa-skeleton>'
          return row
        })
        : []),
    )
  }

  private onParentCollapse(e: Event) {
    if (e instanceof CustomEvent) {
      const { collapsed } = e.detail
      this.setMenuCollapsed(collapsed)
    }
  }

  private setMenuCollapsed(collapsed: boolean) {
    const nav = this.shadowRoot?.querySelector('.nav-enterprise-menu')
    if (nav) {
      nav.classList.toggle('collapsed', collapsed)
    }
  }
}

if (!customElements.get('ts-nav-enterprise-menu')) {
  customElements.define('ts-nav-enterprise-menu', NavEnterpriseMenu)
}
export { NavEnterpriseMenu }
